import { useState } from "react";
import type { ClubPreview, ClubView, SeasonReportView } from "../game/types";
import { Crest, Panel, PositionChip, Stars, StrengthBadge, strengthColor } from "../ui/atoms";
import { CartaoCompartilhavel } from "../ui/CartaoCompartilhavel";
import type { DadosDeCartao } from "../ui/cartoes";
import { useDialogoDeAtalhos } from "../ui/useAtalhos";
import { conquistaDoMotor, dinheiro, numero, rotuloDoMotor, useT, type Chave } from "../i18n";

/**
 * Qual das cinco frases da diretoria vale para este balanço.
 *
 * A frase saiu do save, onde era um parágrafo em português congelado na
 * redação do dia em que foi gravado. O balanço já carrega tudo o que ela
 * precisa (o veredito, se houve demissão e quantos títulos vieram), então ela
 * é escolhida aqui e escrita no idioma de quem está lendo.
 */
function chaveDoVeredito(report: SeasonReportView): Chave {
  if (report.fired) return "fimDeTemporada.veredito.demitido";
  if (report.titles.length > 0) return "fimDeTemporada.veredito.titulo";
  if (report.verdict === "acima") return "fimDeTemporada.veredito.acima";
  if (report.verdict === "cumpriu") return "fimDeTemporada.veredito.cumpriu";
  return "fimDeTemporada.veredito.abaixo";
}

/**
 * Fim de temporada.
 *
 * Antes disto o ano virava em silêncio: o campeão saía numa notícia solta e o
 * treinador só descobria pelo elenco que a base tinha subido gente e que havia
 * contrato vencendo. Aqui está tudo o que aconteceu, e as duas decisões que só
 * cabem nesta hora: renovar quem está de saída e responder a quem chamou.
 */
export function SeasonEnd({
  report,
  clube,
  busy,
  onRenew,
  onTakeJob,
  onContinue,
}: {
  report: SeasonReportView;
  /** Identidade do clube, que o balanço não carrega, para o escudo do cartão. */
  clube: Pick<ClubView["club"], "short" | "colors">;
  busy: boolean;
  onRenew: (playerId: string) => void;
  onTakeJob: (clubId: string) => void;
  onContinue: () => void;
}) {
  const t = useT();
  const [cartao, setCartao] = useState(false);
  // O clube aberto na prévia, antes de assumir.
  const [previa, definirPrevia] = useState<string | null>(null);
  const previaAberta = report.offers.find((oferta) => oferta.clubId === previa) ?? null;

  // Demitido com convites na mesa, ninguém segue sem escolher onde recomeçar.
  const podeContinuar = !busy && !(report.fired && report.offers.length > 0);

  useDialogoDeAtalhos({
    nome: "Fim de temporada",
    nivel: 30,
    aoFechar: podeContinuar ? onContinue : null,
    atalhos: {
      enter: () => {
        if (podeContinuar) onContinue();
      },
      c: () => {
        if (report.titles.length > 0) setCartao(true);
      },
    },
  });

  const cartaoDoTitulo: DadosDeCartao = {
    tipo: "titulo",
    contexto: t("fimDeTemporada.contextoDoCartao", { temporada: report.season }),
    clube: { nome: report.clubName, sigla: clube.short, cores: clube.colors },
    titulos: report.titles,
    liga: report.leagueName,
    posicao: report.position,
    pontos: report.points,
    vitorias: report.wins,
    saldo: report.goalsFor - report.goalsAgainst,
  };

  const cor =
    report.verdict === "acima"
      ? "var(--success)"
      : report.verdict === "cumpriu"
        ? "var(--warning)"
        : "var(--danger)";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--bg)",
        overflow: "auto",
        padding: "var(--gutter)",
        zIndex: 30,
      }}
    >
      <div style={{ maxWidth: 940, margin: "0 auto", paddingBottom: 40 }}>
        <header style={{ textAlign: "center", padding: "26px 0 18px" }}>
          <div className="label-muted">
            {t("fimDeTemporada.encerrada", { temporada: report.season })}
          </div>
          <h1 style={{ fontSize: 30, margin: "6px 0 4px" }}>{report.clubName}</h1>
          <div className="row" style={{ gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <span className="num" style={{ fontSize: 22, fontWeight: 800, color: cor }}>
              {t("fimDeTemporada.posicaoValor", { posicao: report.position })}
            </span>
            <span className="muted">{t("fimDeTemporada.naLiga", { liga: report.leagueName })}</span>
          </div>
        </header>

        {report.titles.length > 0 && (
          <div
            className="card"
            style={{
              padding: "14px 18px",
              marginBottom: 14,
              borderColor: "var(--accent)",
              textAlign: "center",
            }}
          >
            <div className="label" style={{ color: "var(--accent)" }}>
              {report.titles.length === 1
                ? t("fimDeTemporada.tituloConquistado")
                : t("fimDeTemporada.titulosConquistados")}
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>
              {report.titles.join(" · ")}
            </div>
            <button className="ghost" style={{ marginTop: 10 }} onClick={() => setCartao(true)}>
              {t("fimDeTemporada.compartilharTitulo")}
            </button>
          </div>
        )}

        {report.achievements.length > 0 && (
          <Panel
            title={
              report.achievements.length === 1
                ? t("fimDeTemporada.conquistaNova")
                : t("fimDeTemporada.conquistasNovas")
            }
            action={<span className="label-muted">{t("fimDeTemporada.verNaCarreira")}</span>}
          >
            <div
              className="grid"
              style={{ gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 10 }}
            >
              {report.achievements.map((conquista) => {
                // O motor manda a chave e guarda o identificador. O nome e a
                // descrição são deste dicionário, e é por isso que a conquista
                // aparece no idioma de quem está lendo.
                const { nome, descricao } = conquistaDoMotor(conquista.key);
                return (
                  <div
                    key={conquista.id}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 8,
                      background: "var(--primary-glow)",
                      border: "1px solid var(--primary)",
                    }}
                  >
                    <strong style={{ fontSize: 13 }}>{nome}</strong>
                    <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>
                      {descricao}
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>
        )}

        <Panel title={t("fimDeTemporada.oQueADiretoriaAchou")}>
          <p style={{ margin: 0, color: report.fired ? "var(--danger)" : "inherit" }}>
            {t(chaveDoVeredito(report), {
              objetivo: rotuloDoMotor(report.targetLabelKey),
              posicao: report.position,
            })}
          </p>
        </Panel>

        <div
          className="grid"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 14, marginTop: 14 }}
        >
          <Panel title={t("fimDeTemporada.campanha")}>
            <div
              className="grid"
              style={{ gridTemplateColumns: "repeat(auto-fit, minmax(72px, 1fr))", gap: 10 }}
            >
              <Numero label={t("fimDeTemporada.pontos")} valor={report.points} destaque />
              <Numero label={t("fimDeTemporada.jogos")} valor={report.played} />
              <Numero label={t("fimDeTemporada.vitorias")} valor={report.wins} />
              <Numero label={t("fimDeTemporada.empates")} valor={report.draws} />
              <Numero label={t("fimDeTemporada.derrotas")} valor={report.losses} />
              <Numero
                label={t("fimDeTemporada.saldo")}
                valor={report.goalsFor - report.goalsAgainst}
              />
            </div>
            <div className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
              {t("fimDeTemporada.meta", {
                meta: rotuloDoMotor(report.targetLabelKey),
                posicao: report.target,
              })}
            </div>
          </Panel>

          <Panel title={t("fimDeTemporada.caixaDaTemporada")}>
            <Linha
              rotulo={t("fimDeTemporada.receita")}
              valor={dinheiro(report.finance.income)}
              cor="var(--success)"
            />
            <Linha
              rotulo={t("fimDeTemporada.despesa")}
              valor={dinheiro(report.finance.expense)}
              cor="var(--danger)"
            />
            <Linha
              rotulo={t("fimDeTemporada.resultado")}
              valor={dinheiro(report.finance.balance)}
              cor={report.finance.balance >= 0 ? "var(--success)" : "var(--danger)"}
              forte
            />
            <Linha rotulo={t("fimDeTemporada.caixaHoje")} valor={dinheiro(report.finance.cash)} />
            {report.finance.debt > 0 && (
              <Linha
                rotulo={t("fimDeTemporada.divida")}
                valor={dinheiro(report.finance.debt)}
                cor="var(--danger)"
              />
            )}
          </Panel>

          <Panel title={t("fimDeTemporada.destaques")}>
            {report.topScorer ? (
              <Linha
                rotulo={t("fimDeTemporada.artilheiro")}
                valor={t("fimDeTemporada.artilheiroValor", {
                  nome: report.topScorer.name,
                  gols: report.topScorer.goals,
                })}
              />
            ) : (
              <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                {t("fimDeTemporada.ninguemDestaque")}
              </p>
            )}
            {report.bestRated && (
              <Linha
                rotulo={t("fimDeTemporada.melhorNota")}
                valor={t("fimDeTemporada.melhorNotaValor", {
                  nome: report.bestRated.name,
                  nota: report.bestRated.rating.toFixed(1),
                })}
              />
            )}
          </Panel>
        </div>

        {report.youth.length > 0 && (
          <Panel
            title={t("fimDeTemporada.subiramDaBase")}
            action={
              <span className="label-muted">
                {t("fimDeTemporada.garotos", { total: report.youth.length })}
              </span>
            }
            padded={false}
          >
            <table>
              <thead>
                <tr>
                  <th style={{ width: 48 }}>{t("fimDeTemporada.colunas.pos")}</th>
                  <th>{t("fimDeTemporada.colunas.jogador")}</th>
                  <th className="right" style={{ width: 60 }}>
                    {t("fimDeTemporada.colunas.idade")}
                  </th>
                  <th className="right" style={{ width: 60 }}>
                    {t("fimDeTemporada.colunas.forca")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.youth.map((garoto) => (
                  <tr key={garoto.playerId} className="player">
                    <td>
                      <PositionChip pos={garoto.pos} />
                    </td>
                    <td style={{ fontWeight: 600 }}>{garoto.name}</td>
                    <td className="right num">{garoto.age}</td>
                    <td className="right">
                      <StrengthBadge value={garoto.strength} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        )}

        {report.expiring.length > 0 && !report.fired && (
          <Panel
            title={t("fimDeTemporada.contratosAcabando")}
            action={<span className="label-muted">{t("fimDeTemporada.renoveAntes")}</span>}
            padded={false}
          >
            <table>
              <thead>
                <tr>
                  <th style={{ width: 48 }}>{t("fimDeTemporada.colunas.pos")}</th>
                  <th>{t("fimDeTemporada.colunas.jogador")}</th>
                  <th className="right" style={{ width: 56 }}>
                    {t("fimDeTemporada.colunas.idade")}
                  </th>
                  <th className="right" style={{ width: 56 }}>
                    {t("fimDeTemporada.colunas.forca")}
                  </th>
                  <th className="right" style={{ width: 100 }}>
                    {t("fimDeTemporada.colunas.salario")}
                  </th>
                  <th style={{ width: 96 }} />
                </tr>
              </thead>
              <tbody>
                {report.expiring.map((jogador) => (
                  <tr key={jogador.playerId} className="player">
                    <td>
                      <PositionChip pos={jogador.pos} />
                    </td>
                    <td style={{ fontWeight: 600 }}>{jogador.name}</td>
                    <td className="right num">{jogador.age}</td>
                    <td className="right">
                      <StrengthBadge value={jogador.strength} />
                    </td>
                    <td className="right num">{dinheiro(jogador.salary)}</td>
                    <td className="right">
                      {jogador.renewing ? (
                        <span className="label">{t("comum.naFila")}</span>
                      ) : (
                        <button
                          className="ghost"
                          disabled={busy}
                          onClick={() => onRenew(jogador.playerId)}
                          style={{ minHeight: 26, padding: "2px 10px", fontSize: 11 }}
                        >
                          {t("fimDeTemporada.renovar")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        )}

        {report.offers.length > 0 && (
          <Panel
            title={
              report.fired ? t("fimDeTemporada.quemChamou") : t("fimDeTemporada.sondagens")
            }
            action={
              <span className="label-muted">
                {report.fired
                  ? t("fimDeTemporada.escolhaOndeRecomecar")
                  : t("fimDeTemporada.aceitarTroca")}
              </span>
            }
          >
            <div className="grid" style={{ gap: 10 }}>
              {report.offers.map((oferta) => (
                <div
                  key={oferta.clubId}
                  className="row oferta-de-clube"
                  // Clicar no clube abre a prévia; os botões fazem o que dizem.
                  onClick={(evento) => {
                    if ((evento.target as HTMLElement).closest("button")) return;
                    if (oferta.preview) definirPrevia(oferta.clubId);
                  }}
                  style={{
                    gap: 12,
                    padding: "10px 12px",
                    background: "var(--surface-2)",
                    borderRadius: 8,
                    cursor: oferta.preview ? "pointer" : undefined,
                  }}
                >
                  <Crest colors={oferta.colors} short={oferta.clubShort} size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700 }}>{oferta.clubName}</div>
                    <div className="muted" style={{ fontSize: 11.5 }}>
                      {t("fimDeTemporada.ofertaLinha", {
                        liga: oferta.leagueName,
                        pais: oferta.countryName,
                        objetivo: rotuloDoMotor(oferta.objectiveKey),
                      })}
                    </div>
                    <div className="label-muted" style={{ marginTop: 3 }}>
                      {t("fimDeTemporada.caixaDe", { valor: dinheiro(oferta.budget) })}
                    </div>
                  </div>
                  {oferta.preview && (
                    <button className="ghost" disabled={busy} onClick={() => definirPrevia(oferta.clubId)}>
                      {t("fimDeTemporada.verClube")}
                    </button>
                  )}
                  <button
                    className="primary"
                    disabled={busy}
                    onClick={() => onTakeJob(oferta.clubId)}
                  >
                    {t("fimDeTemporada.assumir")}
                  </button>
                </div>
              ))}
            </div>
          </Panel>
        )}

        {previaAberta?.preview && (
          <PreviaDoClube
            oferta={previaAberta}
            previa={previaAberta.preview}
            busy={busy}
            aoAssumir={() => onTakeJob(previaAberta.clubId)}
            aoFechar={() => definirPrevia(null)}
          />
        )}

        {report.fired && report.offers.length === 0 && (
          <Panel title={t("fimDeTemporada.semClube")}>
            <p className="muted" style={{ margin: 0 }}>
              {t("fimDeTemporada.ninguemChamou")}
            </p>
          </Panel>
        )}

        <button
          className="primary"
          style={{ width: "100%", marginTop: 18, minHeight: 46, fontSize: 15 }}
          disabled={!podeContinuar}
          onClick={onContinue}
        >
          {report.fired && report.offers.length > 0
            ? t("fimDeTemporada.escolhaUmClube")
            : t("fimDeTemporada.comecarTemporada", { temporada: report.season + 1 })}
        </button>
      </div>

      {cartao && (
        <CartaoCompartilhavel dados={cartaoDoTitulo} aoFechar={() => setCartao(false)} />
      )}
    </div>
  );
}

/**
 * O clube que chamou, por dentro, antes de assinar.
 *
 * Números que decidem a escolha em cima (caixa, dívida, folha, estádio, força e
 * idade do elenco) e o elenco inteiro embaixo, do goleiro ao ataque. Assumir
 * fica aqui também, para não precisar fechar e procurar o botão de novo.
 */
function PreviaDoClube({
  oferta,
  previa,
  busy,
  aoAssumir,
  aoFechar,
}: {
  oferta: SeasonReportView["offers"][number];
  previa: ClubPreview;
  busy: boolean;
  aoAssumir: () => void;
  aoFechar: () => void;
}) {
  const t = useT();
  useDialogoDeAtalhos({ nome: "Prévia do clube", nivel: 30, aoFechar });

  return (
    <div
      role="dialog"
      aria-label={oferta.clubName}
      onClick={aoFechar}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--gutter)",
        zIndex: 30,
      }}
    >
      <div
        className="card"
        onClick={(evento) => evento.stopPropagation()}
        style={{ width: "min(720px, 100%)", maxHeight: "88vh", display: "flex", flexDirection: "column" }}
      >
        <header className="row" style={{ gap: 12, flexWrap: "wrap", padding: "14px 16px", borderBottom: "1px solid var(--border)" }}>
          <Crest colors={oferta.colors} short={oferta.clubShort} size={44} />
          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            <h2 style={{ fontSize: 18 }}>{oferta.clubName}</h2>
            <div className="muted" style={{ fontSize: 12 }}>
              {t("fimDeTemporada.ofertaLinha", {
                liga: oferta.leagueName,
                pais: oferta.countryName,
                objetivo: rotuloDoMotor(oferta.objectiveKey),
              })}
            </div>
          </div>
          <span className="row" style={{ gap: 6, marginLeft: "auto" }}>
            <Stars level={previa.reputation} />
            <span className="label">{rotuloDoMotor(previa.reputationKey)}</span>
          </span>
        </header>

        <div className="previa-numeros">
          <NumeroDaPrevia rotulo={t("fimDeTemporada.previaCaixa")} valor={dinheiro(oferta.budget)} />
          <NumeroDaPrevia
            rotulo={t("fimDeTemporada.previaDivida")}
            valor={dinheiro(previa.debt)}
            cor={previa.debt > 0 ? "var(--danger)" : undefined}
          />
          <NumeroDaPrevia rotulo={t("fimDeTemporada.previaFolha")} valor={dinheiro(previa.wagesPerRound)} />
          <NumeroDaPrevia
            rotulo={t("fimDeTemporada.previaEstadio")}
            valor={t("hub.lugares", { capacidade: numero(previa.stadiumCapacity) })}
          />
          <NumeroDaPrevia
            rotulo={t("fimDeTemporada.previaPosicao")}
            valor={previa.position > 0 ? t("hub.posicaoValor", { posicao: previa.position }) : "-"}
          />
          <NumeroDaPrevia
            rotulo={t("fimDeTemporada.previaForca")}
            valor={String(previa.squadStrength)}
            cor={strengthColor(previa.squadStrength)}
          />
          <NumeroDaPrevia rotulo={t("fimDeTemporada.previaIdade")} valor={numero(previa.averageAge)} />
        </div>

        <div className="label-muted" style={{ padding: "10px 16px 6px" }}>
          {t("fimDeTemporada.previaElenco", { total: previa.squad.length })}
        </div>
        <div className="scroll" style={{ flex: 1, minHeight: 0, padding: "0 10px 6px" }}>
          <table className="previa-elenco">
            <tbody>
              {previa.squad.map((jogador) => (
                <tr key={jogador.id}>
                  <td style={{ width: 48 }}>
                    <PositionChip pos={jogador.pos} />
                  </td>
                  <td style={{ fontWeight: 600 }}>{jogador.name}</td>
                  <td className="muted num" style={{ width: 70 }}>
                    {t("fimDeTemporada.previaAnos", { idade: jogador.age })}
                  </td>
                  <td style={{ width: 40, textAlign: "right" }}>
                    <StrengthBadge value={jogador.strength} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <footer className="row" style={{ gap: 8, justifyContent: "flex-end", padding: "12px 16px", borderTop: "1px solid var(--border)" }}>
          <button className="ghost" onClick={aoFechar}>
            {t("comum.fechar")}
          </button>
          <button className="primary" disabled={busy} onClick={aoAssumir}>
            {t("fimDeTemporada.assumir")}
          </button>
        </footer>
      </div>
    </div>
  );
}

function NumeroDaPrevia({ rotulo, valor, cor }: { rotulo: string; valor: string; cor?: string }) {
  return (
    <div style={{ padding: "6px 0", borderBottom: "1px solid var(--border)", minWidth: 0 }}>
      <div className="muted" style={{ fontSize: 11.5 }}>
        {rotulo}
      </div>
      <div className="num" style={{ fontWeight: 700, color: cor, whiteSpace: "nowrap" }}>
        {valor}
      </div>
    </div>
  );
}

function Numero({ label, valor, destaque = false }: { label: string; valor: number; destaque?: boolean }) {
  return (
    <div>
      <div className="label-muted">{label}</div>
      <div
        className="num"
        style={{
          fontSize: destaque ? 22 : 17,
          fontWeight: 800,
          marginTop: 2,
          color: destaque ? "var(--primary)" : "inherit",
        }}
      >
        {valor}
      </div>
    </div>
  );
}

function Linha({
  rotulo,
  valor,
  cor,
  forte = false,
}: {
  rotulo: string;
  valor: string;
  cor?: string;
  forte?: boolean;
}) {
  return (
    <div
      className="row"
      style={{
        justifyContent: "space-between",
        gap: 12,
        padding: "5px 0",
        borderBottom: "1px solid var(--border)",
      }}
    >
      <span className="muted" style={{ fontSize: 12 }}>
        {rotulo}
      </span>
      <span className="num" style={{ fontWeight: forte ? 800 : 600, color: cor }}>
        {valor}
      </span>
    </div>
  );
}
